import React from "react";
import { useIntl } from "react-intl";
import SettingsCheckbox from "../SettingsCheckbox";
import LineStyleButton from "../../LineStyleButton";
import { hasPlotWidth } from "../indicatorDraft";
import "./StylesTab.sass";

const NO_PLOTS = [];
const noop = () => {};

/**
 * The Styles tab (po-chart-visual-spec.md §5.4, 07-indicator-settings-styles.png):
 * per line a checkbox, its name and the «colour + width» button (columns
 * with a down colour get a second colour button, a background only a colour,
 * a line without a colour only the checkbox);
 * per level of an oscillator a checkbox, its value as a field and the button.
 *
 * @param {object}   props
 * @param {object[]} props.plots - registry plots of the indicator
 * @param {object}   props.plotStyles - { [plot key]: { isVisible, color, downColor, width } }
 * @param {object[]} props.levels - [{ value: text, color, width, isVisible }]
 * @param {object[]} [props.plotsAfterLevels] - rows like `plots`, under the levels
 * @param {string[]} props.palette - engine.getLinePalette()
 * @param {(key: string, change: object) => void} props.onPlotChange
 * @param {(index: number, change: object) => void} props.onLevelChange
 * @param {(index: number) => void} [props.onLevelCommit] - a level field lost focus
 */
const StylesTab = ({
  plots,
  plotStyles,
  levels,
  plotsAfterLevels = NO_PLOTS,
  palette,
  onPlotChange,
  onLevelChange,
  onLevelCommit = noop,
}) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const showLabel = t("futures.chart.indicator_settings.show");
  const levelLabel = t("futures.chart.indicator_settings.level");

  const renderPlot = (plot) => {
    const style = plotStyles[plot.key];

    return (
      <div key={plot.key} className="styles-tab__row">
        <SettingsCheckbox
          isChecked={style.isVisible}
          onChange={(isVisible) => onPlotChange(plot.key, { isVisible })}
          label={`${showLabel} ${plot.label}`}
        />
        <span className="styles-tab__name" title={plot.label}>
          {plot.label}
        </span>
        <span className="styles-tab__buttons">
          {style.color && (
            <LineStyleButton
              color={style.color}
              width={hasPlotWidth(plot) ? style.width : null}
              palette={palette}
              onChange={(change) => onPlotChange(plot.key, change)}
              label={plot.label}
            />
          )}
          {style.downColor && (
            <LineStyleButton
              color={style.downColor}
              palette={palette}
              onChange={({ color }) => onPlotChange(plot.key, { downColor: color })}
              label={`${plot.label} ${t("futures.chart.indicator_settings.down_color")}`}
            />
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="styles-tab">
      {plots.map(renderPlot)}
      {levels.map((level, index) => {
        const label = `${levelLabel} ${index + 1}`;

        return (
          // Levels have no ids; their number and order never change in the modal
          <div key={index} className="styles-tab__row">
            <SettingsCheckbox
              isChecked={level.isVisible}
              onChange={(isVisible) => onLevelChange(index, { isVisible })}
              label={`${showLabel} ${label}`}
            />
            <input
              type="text"
              className="styles-tab__level"
              inputMode="decimal"
              autoComplete="off"
              aria-label={label}
              value={level.value}
              onChange={(event) => onLevelChange(index, { value: event.target.value })}
              onBlur={() => onLevelCommit(index)}
            />
            <span className="styles-tab__buttons">
              <LineStyleButton
                color={level.color}
                width={level.width}
                palette={palette}
                onChange={(change) => onLevelChange(index, change)}
                label={label}
              />
            </span>
          </div>
        );
      })}
      {plotsAfterLevels.map(renderPlot)}
    </div>
  );
};

export default StylesTab;
